import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "max-h-[70vh] min-h-[22rem] overflow-y-auto px-8 space-y-4" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center justify-center py-16"
}
const _hoisted_3 = {
  key: 1,
  class: "rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
}
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { class: "mt-1" }
const _hoisted_6 = { class: "mt-2 flex items-center gap-2" }
const _hoisted_7 = {
  key: 0,
  class: "flex items-center justify-center py-16"
}
const _hoisted_8 = {
  key: 1,
  class: "rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
}
const _hoisted_9 = { class: "flex items-center justify-between" }
const _hoisted_10 = { class: "font-semibold" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = {
  key: 1,
  class: "py-6 text-center text-sm italic text-gray-400"
}
const _hoisted_13 = { class: "sticky bottom-0 flex items-center justify-between gap-4 border-t border-gray-200 bg-white px-8 py-4" }

import { ref, computed, watch } from 'vue';
import { format, parseISO } from 'date-fns';
import { useQueryClient } from '@tanstack/vue-query';
import { VisuallyHidden } from 'radix-vue';
import { useToast } from 'vue-toastification';
import { Plus } from 'lucide-vue-next';
import { Dialog, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import CustomButton from '@/components/ui/CustomButton.vue';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import Spinner from '@/components/ui/Spinner.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { queryKeys, useBackfillSuggestions, useBackfillPigTallySeries, useProductionGroupTimeline } from '@/plugins/store/actions/queries/production-groups';
import { Barn } from '@/types';
import useProductionGroupBackfill, { formatDuration, invalidRangeSuggestionIds, overlappingSuggestionIds, type BackfillDraft } from './useProductionGroupBackfill';
import BackfillTimeline from './BackfillTimeline.vue';
import BackfillSuggestionTable from './BackfillSuggestionTable.vue';

const EARLIEST_GROUP_DATE = '2020-01-01';

export default /*@__PURE__*/_defineComponent({
  __name: 'BackfillWizardModal',
  props: /*@__PURE__*/_mergeModels({
    barn: {},
    initialSectionId: {}
  }, {
    "modelValue": { type: Boolean },
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any) {

const props = __props;

const showModal = _useModel<boolean>(__props, 'modelValue');

const queryClient = useQueryClient();
const toast = useToast();

// Local calendar day, not UTC - the farmer's own wall clock is what "today" means when deciding
// whether a group just ended, same as every other Production Group date (ManageProductionGroupModal's
// own todayIso) already uses.
const today = format(new Date(), 'yyyy-MM-dd');

const sectionOptions = computed(() => store.state.sections?.filter((s) => s.barn_id === props.barn.barn_id) ?? []);
const sectionId = ref<number | null>(null);
const fromDateOverride = ref<string | null>(null);

const { data, isLoading, isError: isDetectionError, refetch: refetchDetection } = useBackfillSuggestions(sectionId, fromDateOverride);

// A detection that actually ran. Null while the request is in flight, and null when the backend
// reports it could not resolve a from-date at all - that case is a banner, not an empty table.
const detection = computed(() => (data.value && !data.value.no_detection_reason ? data.value : null));

const backfill = useProductionGroupBackfill(today);

// Loads the draft from a detection result exactly once per Section/from-date request, not on every
// change to `data` - `getBackfillSuggestions` nests under the same query-key root the "Create"
// handler invalidates below, so a background refetch of the SAME request (after a create, partial
// failure included) would otherwise replace an in-progress draft - discarding whatever the farmer
// hasn't created yet - out from under them the moment it resolves.
let loadedForRequestKey: string | null = null;
watch(data, () => {
    // `data` goes undefined the moment sectionId/fromDateOverride changes to a key with nothing
    // cached yet, before the new request has actually resolved. Recording the key here regardless
    // would mark the new request "already loaded" against an empty draft, and the real response
    // arriving moments later would then be silently discarded by the check below.
    if (data.value === undefined) return;
    const requestKey = `${sectionId.value}:${fromDateOverride.value}`;
    if (loadedForRequestKey === requestKey) return;
    loadedForRequestKey = requestKey;
    backfill.load(detection.value?.suggestions ?? []);
});

// A genuinely new detection target discards any in-progress draft, same as re-opening the wizard
// would - this is what actually clears the way for the watcher above to load the new request's
// result once it arrives (also marks nothing as loaded yet for that request).
watch([sectionId, fromDateOverride], () => {
    loadedForRequestKey = null;
    backfill.load([]);
});

// DatePicker emits a UTC ISO timestamp for the selected local midnight (FlatpickrWrapper's
// `dateFormat: 'Z'`) - slicing the first 10 characters takes the UTC calendar day, not the local
// one the farmer actually picked, which differ for any positive-offset timezone (Denmark included:
// picking October 22 there emits `2026-10-21T22:00:00.000Z`, so a slice would read October 21).
// Converting back through parseISO/format, the same way BackfillSuggestionTable's own date handlers
// do, reads the local calendar day instead.
function handleFromDateOverride(value: string | null) {
    fromDateOverride.value = value ? format(parseISO(value), 'yyyy-MM-dd') : null;
}

// DatePicker displays its `value` via `new Date(value)`, which reads a bare "yyyy-MM-dd" as UTC
// midnight - the wrong calendar day once formatted back in a negative-offset timezone. Routing a
// date-only value through parseISO (local midnight) before toISOString() hands DatePicker a
// timestamp that displays as the same local day it was given.
function toPickerValue(dateOnly: string): string {
    return parseISO(dateOnly).toISOString();
}
const overrideDisplayValue = computed(() => {
    const dateOnly = fromDateOverride.value || detection.value?.from_date;
    return dateOnly ? toPickerValue(dateOnly) : '';
});

const chartRange = computed(() => (detection.value ? { from: detection.value.from_date, to: today } : null));
const { data: tally, isLoading: isTallyLoading, isError: isTallyError, refetch: refetchTally } = useBackfillPigTallySeries(sectionId, chartRange);

// The groups lane has to show every group that could overlap a suggestion, and detection can be
// pointed at any from-date the farmer types, so the existing-group read is deliberately NOT clipped
// to the detection range - it goes back to this fixed floor instead, well before any farm on the
// platform has Production Group data, which is as unbounded as a bounded read can practically be.
const timelineRequest = computed(() =>
    sectionId.value !== null ? { sectionId: sectionId.value, barnId: props.barn.barn_id, from: EARLIEST_GROUP_DATE, to: today } : null
);
const { data: existingGroups, isLoading: isExistingGroupsLoading, isError: isExistingGroupsError, refetch: refetchExistingGroups } = useProductionGroupTimeline(timelineRequest);

// Gates the chart/table body separately from the outer detection spinner above: detection can
// resolve while these two still have their own request in flight for the section just picked.
const isDetailLoading = computed(() => isTallyLoading.value || isExistingGroupsLoading.value);

// A failed existing-groups read is the one that matters for safety - without it, overlap validation
// has nothing to check against, so hasOverlap below would silently wave every suggestion through. A
// failed tally read only loses the chart. Both still gate the whole body, since a farmer fixing a
// suggestion against a chart that may be showing stale/wrong data is its own kind of trap.
const hasDetailError = computed(() => isTallyError.value || isExistingGroupsError.value);
function retryDetail() {
    return Promise.all([refetchTally(), refetchExistingGroups()]);
}

// Same rules the table uses to accent a blocked row (see useProductionGroupBackfill.ts) - blocks
// the create action outright rather than only warning: an overlap would fail the backend's own
// validation on create anyway, so this only saves the round trip. A same-day range is rejected here
// client-side (see analysis in the composable) because the backend does NOT reject that one on its
// own - skipping this check would create it.
const hasOverlap = computed(
    () =>
        overlappingSuggestionIds(backfill.suggestions.value, existingGroups.value || [], today).size > 0 ||
        invalidRangeSuggestionIds(backfill.suggestions.value).size > 0
);

watch(sectionId, () => {
    fromDateOverride.value = null;
});

watch(showModal, (value) => {
    if (value) {
        sectionId.value = props.initialSectionId ?? null;
    } else {
        // Deferred past the dialog's close animation, so the farmer does not watch the wizard empty
        // itself out on the way off screen.
        setTimeout(() => {
            sectionId.value = null;
            fromDateOverride.value = null;
            backfill.load([]);
        }, 300);
    }
});

function buildGroupName(suggestion: BackfillDraft) {
    return i18n.global.t('views.monitoring.groupOverview.modals.backfill.autoGroupName', { date: suggestion.start });
}

const isCreating = ref(false);

async function handleCreateAll() {
    const locationId = sectionId.value;

    if (isCreating.value || backfill.acceptedCount.value === 0 || locationId === null) {
        return;
    }

    isCreating.value = true;

    // Settled rather than awaited as a batch: each create is validated against existing groups on
    // its own, so some can be rejected while the rest land - which is what the partial-failure
    // message below reports.
    const acceptedSuggestions = backfill.accepted.value;
    const results = await Promise.allSettled(
        acceptedSuggestions.map((suggestion) =>
            store.dispatch(ActionType.CreateProductionGroup, {
                options: { skipHandleError: true },
                form: {
                    location_id: locationId,
                    name: buildGroupName(suggestion),
                    note: null,
                    start: suggestion.start,
                    end: suggestion.end,
                    is_favorite: false,
                    // Only an untouched detection counts as auto-generated - a suggestion the farmer
                    // edited, or added by hand, reflects their own judgment and reads as manual.
                    is_auto_generated: !suggestion.customized,
                },
            })
        )
    );

    const failedCount = results.filter((r) => r.status === 'rejected').length;
    const succeededCount = results.length - failedCount;

    // Drop each succeeded row from the draft regardless of overall outcome, same as a farmer
    // discarding it by hand - once created, the row and the group it just became carry the same
    // dates, so leaving it accepted would read as a self-overlap the moment existingGroups refetches
    // below, blocking the farmer from fixing and retrying whatever is actually still left.
    results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
            backfill.discard(acceptedSuggestions[index].id);
        }
    });

    // On full success, drop the draft *before* invalidating: `getBackfillSuggestions` and
    // `getProductionGroupTimeline` share the same query root and refetch independently, so for a
    // moment the timeline read can already carry the just-created groups while this draft's
    // `accepted` rows still name the exact same ranges - hasOverlap (computed from both) would read
    // that as a brand-new collision and flash the "overlapping dates" banner right as the modal is
    // closing. Clearing first removes one side of that comparison instead of racing the two queries.
    if (failedCount === 0) {
        backfill.load([]);
    }

    await queryClient.invalidateQueries({ queryKey: queryKeys.getProductionGroupsRoot });

    if (failedCount === 0) {
        toast.success(i18n.global.t('views.monitoring.groupOverview.modals.backfill.successMessages.groupsWereCreated', { count: succeededCount }));
        showModal.value = false;
    } else {
        toast.error(i18n.global.t('views.monitoring.groupOverview.modals.backfill.errors.partialFailure', { succeeded: succeededCount, failed: failedCount }));
    }

    isCreating.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showModal).value = $event)),
    disabled: isCreating.value,
    class: "pb-0 px-0 sm:max-w-[900px]"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogHeader), { class: "pt-8 pb-4 px-8" }, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogTitle), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.title')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(VisuallyHidden), null, {
            default: _withCtx(() => [
              _createVNode(_unref(DialogDescription), { class: "pt-4" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.description')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(CustomSelect, {
          value: sectionId.value,
          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((sectionId).value = $event)),
          items: sectionOptions.value,
          "item-text": "name",
          "item-value": "id",
          label: _ctx.$t('views.monitoring.groupOverview.modals.backfill.fields.section'),
          required: "",
          "allow-empty": false,
          disabled: isCreating.value
        }, null, 8, ["value", "items", "label", "disabled"]),
        (sectionId.value !== null)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (_unref(isLoading))
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createVNode(Spinner, { class: "h-8 w-8" })
                  ]))
                : (_unref(isDetectionError))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _createElementVNode("p", null, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.errors.detectionFailed')), 1),
                      _createElementVNode("button", {
                        type: "button",
                        class: "mt-2 text-xs font-semibold text-red-800 hover:text-red-900",
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(refetchDetection)()))
                      }, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.errors.retry')), 1)
                    ]))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["rounded-lg border p-4 text-sm", _unref(data)?.no_detection_reason ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-gray-200 bg-gray-50 text-gray-700'])
                      }, [
                        (_unref(data)?.no_detection_reason)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_unref(data).no_detection_reason), 1))
                          : (detection.value)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createElementVNode("p", null, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.reasonBanner.detectionMode')), 1),
                                _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.reasonBanner.fromDate', { date: _ctx.$d(_unref(parseISO)(detection.value.from_date), 'short') })), 1)
                              ], 64))
                            : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_6, [
                          _createVNode(DatePicker, {
                            value: overrideDisplayValue.value,
                            resetable: "",
                            disabled: isCreating.value,
                            "aria-label": _ctx.$t('views.monitoring.groupOverview.modals.backfill.fields.fromDateOverride'),
                            "show-overlay": false,
                            "onUpdate:value": handleFromDateOverride
                          }, null, 8, ["value", "disabled", "aria-label"])
                        ])
                      ], 2),
                      (detection.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            (isDetailLoading.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                  _createVNode(Spinner, { class: "h-8 w-8" })
                                ]))
                              : (hasDetailError.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                    _createElementVNode("p", null, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.errors.detailFailed')), 1),
                                    _createElementVNode("button", {
                                      type: "button",
                                      class: "mt-2 text-xs font-semibold text-red-800 hover:text-red-900",
                                      onClick: retryDetail
                                    }, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.errors.retry')), 1)
                                  ]))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                    _createVNode(BackfillTimeline, {
                                      tally: _unref(tally) || [],
                                      "existing-groups": _unref(existingGroups) || [],
                                      suggestions: _unref(backfill).suggestions.value,
                                      from: detection.value.from_date,
                                      today: _unref(today)
                                    }, null, 8, ["tally", "existing-groups", "suggestions", "from", "today"]),
                                    _cache[4] || (_cache[4] = _createElementVNode("hr", { class: "border-gray-200" }, null, -1)),
                                    _createElementVNode("div", _hoisted_9, [
                                      _createElementVNode("h4", _hoisted_10, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.title')), 1),
                                      _createElementVNode("button", {
                                        type: "button",
                                        class: "flex items-center gap-x-1 rounded-lg px-3 py-1.5 text-sky-500 transition-colors hover:bg-sky-400/10 disabled:cursor-default disabled:opacity-50",
                                        disabled: isCreating.value,
                                        onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(backfill).addManual(_unref(existingGroups) || [])))
                                      }, [
                                        _createVNode(_unref(Plus), {
                                          class: "size-4",
                                          "aria-hidden": "true"
                                        }),
                                        _createTextVNode(" " + _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.buttons.addManual')), 1)
                                      ], 8, _hoisted_11)
                                    ]),
                                    (_unref(backfill).suggestions.value.length)
                                      ? (_openBlock(), _createBlock(BackfillSuggestionTable, {
                                          key: 0,
                                          suggestions: _unref(backfill).suggestions.value,
                                          "existing-groups": _unref(existingGroups) || [],
                                          today: _unref(today),
                                          disabled: isCreating.value,
                                          onSetAccepted: _unref(backfill).setAccepted,
                                          onDiscard: _unref(backfill).discard,
                                          onSetStart: _unref(backfill).setStart,
                                          onSetEnd: _unref(backfill).setEnd
                                        }, null, 8, ["suggestions", "existing-groups", "today", "disabled", "onSetAccepted", "onDiscard", "onSetStart", "onSetEnd"]))
                                      : (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.empty')), 1))
                                  ], 64))
                          ], 64))
                        : _createCommentVNode("", true)
                    ], 64))
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("p", {
          class: _normalizeClass(["text-sm", hasOverlap.value ? 'font-medium text-red-700' : 'text-gray-600'])
        }, _toDisplayString(hasOverlap.value
                        ? _ctx.$t('views.monitoring.groupOverview.modals.backfill.stickyBar.overlapBlocked')
                        : _ctx.$t('views.monitoring.groupOverview.modals.backfill.stickyBar.summary', { count: _unref(backfill).acceptedCount.value, duration: _unref(formatDuration)(_unref(backfill).totalDays.value) })), 3),
        _createVNode(CustomButton, {
          loading: isCreating.value,
          disabled: _unref(backfill).acceptedCount.value === 0 || hasOverlap.value || _unref(isExistingGroupsError),
          onClick: handleCreateAll
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.buttons.create', { count: _unref(backfill).acceptedCount.value })), 1)
          ]),
          _: 1
        }, 8, ["loading", "disabled"])
      ])
    ]),
    _: 1
  }, 8, ["modelValue", "disabled"]))
}
}

})